import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Grid,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faBriefcase,
  faFilter,
  faTrash,
  faArchive,
  faSearch,
  faBoxArchive,
} from "@fortawesome/free-solid-svg-icons";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";
import { useConfirm } from "../ui";
const ArchivedEmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const confirm = useConfirm();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = sessionStorage.getItem("token");

        if (!token) {
          setError("Token not found. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(`/api/employees`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch employees");
        }

        const data = await response.json();
        const archivedEmployees = data.filter((employee) => employee.archive);
        setEmployees(archivedEmployees);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Error fetching archived employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleDelete = async (employeeId) => {
    const ok = await confirm({
      title: "Permanently delete employee?",
      message: "This cannot be undone. All records for this employee will be removed.",
      confirmText: "Delete permanently",
      destructive: true,
    });
    if (!ok) return;
    try {
      const token = sessionStorage.getItem("token");

      const response = await fetch(
        `/api/employees/${employeeId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Error deleting employee");
      }

      setEmployees(
        employees.filter((employee) => employee._id !== employeeId)
      );
    } catch (err) {
      console.error("Error deleting employee:", err);
      alert("Error deleting employee");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const filteredEmployees = employees.filter((employee) => {
    switch (filterType) {
      case "employeeid":
        return employee.employeeid?.includes(filterQuery) || !filterQuery;
      case "name":
        return (
          employee.name?.toLowerCase().includes(filterQuery.toLowerCase()) ||
          !filterQuery
        );
      case "managerName":
        return (
          employee.managerName
            ?.toLowerCase()
            .includes(filterQuery.toLowerCase()) || !filterQuery
        );
      case "position":
        return (
          employee.role?.toLowerCase().includes(filterQuery.toLowerCase()) ||
          !filterQuery
        );
      default:
        return true;
    }
  });

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Archived Employees"
        subtitle="View and manage archived employee records"
        icon={<FontAwesomeIcon icon={faArchive} />}
      />

      {/* Stats Row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <StatsCard
            label="Total Archived"
            value={employees.length}
            icon={<FontAwesomeIcon icon={faBoxArchive} />}
            color="#8b8fa3"
            bgColor="#f0f2f8"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatsCard
            label="Filtered Results"
            value={filteredEmployees.length}
            icon={<FontAwesomeIcon icon={faSearch} />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
      </Grid>

      {/* Table Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Search & Filter Bar */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          <TextField
            placeholder="Search archived employees..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ color: "#8b8fa3", fontSize: 14 }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              flex: 1,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "8px",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "#eef0f6" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #eef0f6",
              borderRadius: "8px",
              bgcolor: "#fff",
              px: 1.5,
              height: 40,
              minWidth: 200,
            }}
          >
            <FontAwesomeIcon
              icon={faFilter}
              style={{ color: "#8b8fa3", marginRight: 8, fontSize: 13 }}
            />
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              displayEmpty
              variant="standard"
              disableUnderline
              fullWidth
              sx={{ fontSize: "0.875rem", color: "#5a5f7a" }}
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: "8px", mt: 0.5, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
                },
              }}
            >
              <MenuItem value="">Filter By</MenuItem>
              <MenuItem value="employeeid">Employee ID</MenuItem>
              <MenuItem value="name">Employee Name</MenuItem>
              <MenuItem value="managerName">Manager Name</MenuItem>
              <MenuItem value="position">Position</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Content Area */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: "#667eea" }} />
            <Typography sx={{ mt: 2, color: "#8b8fa3", fontSize: "0.875rem" }}>
              Loading archived employees...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ color: "#EF4444", fontWeight: 600, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem" }}>
              {error}
            </Typography>
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <EmptyState
            title="No Archived Employees Found"
            subtitle="Try adjusting your search or filter criteria"
            icon={<FontAwesomeIcon icon={faArchive} />}
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Employee", "Role", "Manager", "Contact", "Status", "Actions"].map(
                    (header) => (
                      <TableCell
                        key={header}
                        sx={{
                          bgcolor: "#f5f7fb",
                          color: "#5a5f7a",
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          borderBottom: "2px solid #f0f0f5",
                          whiteSpace: "nowrap",
                          py: 1.75,
                        }}
                      >
                        {header}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow
                    key={employee._id}
                    sx={{
                      "&:hover": { bgcolor: "#f5f7fb" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    {/* Employee */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: "#8b8fa3",
                            color: "#fff",
                            width: 36,
                            height: 36,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {employee.name?.charAt(0).toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}
                          >
                            {employee.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                            ID: {employee.employeeid}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Role */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faBriefcase}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.875rem", color: "#5a5f7a" }}>
                          {employee.role || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Manager */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      {employee.managerName || "No manager assigned"}
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faPhone}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.contactNumber || "Not provided"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ py: 2 }}>
                      <StatusChip status="archived" />
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title="Delete Permanently" arrow>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleDelete(employee._id)}
                          startIcon={
                            <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                          }
                          sx={{
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textTransform: "none",
                            borderColor: "#eef0f6",
                            color: "#EF4444",
                            px: 1.5,
                            "&:hover": {
                              borderColor: "#EF4444",
                              bgcolor: "#FEF2F2",
                            },
                          }}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </PageContainer>
  );
};

export default ArchivedEmployeeList;
